import qs from 'query-string'
import React, { useEffect, useState } from 'react'
import {
  Breadcrumb,
  Button,
  Container,
  FormControl,
  InputGroup,
  Row,
  Col,
  Table,
  Badge,
} from 'react-bootstrap'
import { useHistory, useLocation } from 'react-router'
import useAdminUsersSearch from 'hooks/use-admin-users-search'
import useRecentSearches from 'hooks/use-recent-searches'
import UsersSidebar from 'components/users-sidebar'
import { User } from 'interfaces/user'
import { formatDateString } from 'utils/date-utils'

const UsersPage = () => {
  const location = useLocation()
  const params = qs.parse(location.search)
  const [query, setQuery] = useState<string>((params.query as string) || '')
  const history = useHistory()

  // Search Query - only pass query if it's not empty
  const { data, isFetching, refetch } = useAdminUsersSearch(
    params.query || undefined
  )
  const users = data?.users

  // Recent searches
  const { addRecentSearch } = useRecentSearches()

  // Check if any user has contacts data to conditionally show the column
  const hasContactsData = users?.some(
    (user) =>
      user.number_of_contacts !== undefined && user.number_of_contacts !== null
  )

  useEffect(() => {
    setQuery(params.query || '')
    // Only refetch if there's actually a query
    if (params.query) {
      refetch()
    }
  }, [params.query, refetch])

  const handleSearchInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setQuery(event.target.value)
  }

  const handleSelectUserClick = (user: User) => {
    history.push(`/user/${user.id}`)
  }

  const handleSubmit = () => {
    const trimmedQuery = query.trim()
    if (trimmedQuery) {
      addRecentSearch(trimmedQuery)
      history.push(`/users?query=${encodeURIComponent(trimmedQuery)}`)
    } else {
      // If query is empty, go to users page without query param
      history.push('/users')
    }
  }

  return (
    <Container fluid className="mt-5 pt-5 px-4">
      <Row>
        <Col lg={9}>
          <Breadcrumb>
            <Breadcrumb.Item href="/">Home</Breadcrumb.Item>
            <Breadcrumb.Item active href="/users">
              Users
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className="d-flex justify-content-between align-items-center mt-4 mb-4">
            <h3 className="mb-0">Users</h3>
            <Button
              variant="primary"
              onClick={() => history.push('/users/new')}
            >
              Add User
            </Button>
          </div>
          <div className="position-relative mb-4 mt-4">
            <InputGroup>
              <FormControl
                autoComplete="off"
                onChange={handleSearchInputChange}
                onKeyDown={(e: React.KeyboardEvent) => {
                  if (e.code === 'Enter' || e.code === 'NumpadEnter') {
                    handleSubmit()
                  }
                }}
                value={query}
                type="text"
                placeholder="Search"
                aria-label="Search Users"
                autoFocus
              />
              <Button variant="success" onClick={handleSubmit}>
                <ion-icon name="search" />
              </Button>
            </InputGroup>
          </div>
          <Table striped bordered hover responsive variant="dark">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Organization</th>
                <th>Phone</th>
                <th>Created At</th>
                {hasContactsData && <th>User Contact Count</th>}
              </tr>
            </thead>
            <tbody>
              {isFetching ? (
                <tr className="m-2">
                  <td>Searching..</td>
                </tr>
              ) : (
                <>
                  {users?.map((user) => (
                    <tr
                      key={user.id}
                      className="pointer"
                      onClick={() => handleSelectUserClick(user)}
                    >
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>
                        {user.organization_name ? (
                          <div className="d-flex gap-1 flex-wrap">
                            {user.organization_name}
                            <span
                              className={`badge ${
                                user.is_org_admin ? 'bg-success' : 'bg-primary'
                              }`}
                            >
                              {user.is_org_admin ? 'Admin' : 'Member'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>{user.phone_number}</td>
                      <td>{formatDateString(user.created_at)}</td>
                      {hasContactsData && <td>{user.number_of_contacts}</td>}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </Table>
        </Col>
        <Col lg={3}>
          <div className="sticky-top" style={{ top: '120px' }}>
            <UsersSidebar />
          </div>
        </Col>
      </Row>
    </Container>
  )
}

export default UsersPage
